import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  RiHistoryLine, 
  RiCalendarLine, 
  RiMoneyDollarCircleLine, 
  RiUserLine,
  RiCheckLine,
  RiTimeLine,
  RiFileTextLine,
  RiCloseCircleLine
} from 'react-icons/ri';
import rentalExtensionService, { RentalExtensionHistory } from '../../services/rentalExtensionService';
import { showAlert } from '../../utils/alert';

interface RentalExtensionHistoryProps {
  rentalId: number;
  rentalCode: string;
}

const RentalExtensionHistoryComponent: React.FC<RentalExtensionHistoryProps> = ({
  rentalId,
  rentalCode
}) => {
  const { data: history, isLoading, error } = useQuery({
    queryKey: ['rental-extension-history', rentalId],
    queryFn: () => rentalExtensionService.getExtensionHistory(rentalId),
    enabled: !!rentalId
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <RiHistoryLine className="w-6 h-6 text-primary-600 mr-2" />
          <h3 className="text-lg font-medium text-secondary-900">ประวัติการต่อเช่า</h3>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <RiHistoryLine className="w-6 h-6 text-primary-600 mr-2" />
          <h3 className="text-lg font-medium text-secondary-900">ประวัติการต่อเช่า</h3>
        </div>
        <div className="bg-danger-50 border border-danger-200 rounded-md p-4">
          <p className="text-sm text-danger-700">ไม่สามารถโหลดประวัติการต่อเช่าได้</p>
        </div>
      </div>
    );
  }

  if (!history) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'long',
      day: 'numeric'
    });
  };

  // ฟิลด์เงินจาก API เป็น string ("3000.00") เพราะ backend ส่ง Decimal — ต้องแปลงเป็นตัวเลข
  // ก่อนใช้ทุกครั้ง ไม่งั้นทั้งการเทียบค่าและการทดสอบ truthy จะผิด ("0.00" เป็น truthy)
  const toNumber = (amount?: number | string | null) =>
    amount == null || amount === '' ? null : Number(amount);

  // BUG-PG-EXTFEE-ZEROTOTAL: ค่าเช่าใหม่เป็นค่าที่ผู้ใช้กรอก — null = ไม่ได้ขอเปลี่ยน
  // ส่วน 0 = ตั้งใจให้ฟรี จึงต้องพิมพ์เลข 0 ออกมาจริง ไม่กลืนเป็น '-'
  const formatMoney = (amount?: number | string | null) => {
    const n = toNumber(amount);
    return n == null ? '-' : `${n.toLocaleString()} บาท`;
  };

  // ค่าเช่าเดิมเป็นค่าที่ระบบคำนวณจากรายการอุปกรณ์ใน rental — 0 แปลว่า "ไม่มีรายการให้คำนวณ"
  // (rental ที่ไม่มี line item) ไม่ใช่ค่าเช่า 0 บาท จึงแสดงเป็น '-'
  const formatOriginalFee = (amount?: number | string | null) =>
    toNumber(amount) ? formatMoney(amount) : '-';

  const calculateTotalDays = () => {
    const start = new Date(history.original_start_date);
    const end = new Date(history.current_end_date);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <RiHistoryLine className="w-6 h-6 text-primary-600 mr-2" />
            <h3 className="text-lg font-medium text-secondary-900">ประวัติการต่อเช่า - {rentalCode}</h3>
          </div>
          <div className="text-sm text-secondary-500">
            ต่อแล้ว {history.total_extensions} ครั้ง (รวม {history.total_extension_days} วัน)
          </div>
        </div>

        {/* Summary */}
        <div className="bg-secondary-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-secondary-500">วันเริ่มต้นเดิม</p>
              <p className="font-medium">{formatDate(history.original_start_date)}</p>
            </div>
            <div>
              <p className="text-sm text-secondary-500">วันสิ้นสุดปัจจุบัน</p>
              <p className="font-medium">{formatDate(history.current_end_date)}</p>
            </div>
            <div>
              <p className="text-sm text-secondary-500">ระยะเวลารวม</p>
              <p className="font-medium">{calculateTotalDays()} วัน</p>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-secondary-900 mb-4">Timeline การต่อเช่า</h4>
          
          {/* Initial Rental */}
          <div className="relative pb-4">
            <div className="absolute left-4 top-4 -bottom-4 w-0.5 bg-secondary-200"></div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-success-100 rounded-full flex items-center justify-center">
                  <RiCheckLine className="w-4 h-4 text-success-600" />
                </div>
              </div>
              <div className="ml-4 flex-1">
                <div className="bg-white border border-secondary-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-secondary-900">เริ่มการเช่า</h5>
                    <span className="text-sm text-secondary-500">{formatDate(history.original_start_date)}</span>
                  </div>
                  <p className="text-sm text-secondary-600">เริ่มสัญญาเช่าครั้งแรก</p>
                </div>
              </div>
            </div>
          </div>

          {/* Extensions */}
          {history.extensions.map((extension, index) => (
            <div key={extension.id} className="relative pb-4">
              {index < history.extensions.length - 1 && (
                <div className="absolute left-4 top-4 -bottom-4 w-0.5 bg-secondary-200"></div>
              )}
              <div className="flex items-start">
                {/* BUG-S122-EXTSTATE FIX: เดิมอนุมานสถานะจาก `approved_at` ซึ่งผิด —
                    เส้นทาง "ปฏิเสธ" (crud/rental_extension.reject_extension) ก็ประทับ
                    approved_by/approved_at เหมือนกัน → ใบที่ถูกปฏิเสธถูกวาดเหมือนใบที่อนุมัติ
                    (ไอคอนฟ้า + "อนุมัติโดย: <คนที่ปฏิเสธ>") ไม่มีร่องรอยว่าถูกปฏิเสธ.
                    ตอนนี้ backend ส่ง is_approved (tri-state) มาแล้ว จึงอ่านค่าจริง */}
                <div className="flex-shrink-0">
                  <div className={`w-8 h-8 ${
                    extension.is_approved === true ? 'bg-info-100'
                      : extension.is_approved === false ? 'bg-danger-100'
                      : 'bg-warning-100'
                  } rounded-full flex items-center justify-center`}>
                    {extension.is_approved === true ? (
                      <RiCalendarLine className="w-4 h-4 text-info-600" />
                    ) : extension.is_approved === false ? (
                      <RiCloseCircleLine className="w-4 h-4 text-danger-600" />
                    ) : (
                      <RiTimeLine className="w-4 h-4 text-warning-600" />
                    )}
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <div className="bg-white border border-secondary-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-secondary-900">
                        ต่อครั้งที่ {extension.extension_number}
                        {extension.is_approved == null && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-100 text-warning-800">
                            รออนุมัติ
                          </span>
                        )}
                        {extension.is_approved === false && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-danger-100 text-danger-800">
                            ถูกปฏิเสธ
                          </span>
                        )}
                      </h5>
                      <span className="text-sm text-secondary-500">{formatDate(extension.extension_date)}</span>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-secondary-600">
                        <RiCalendarLine className="w-4 h-4 mr-2 text-secondary-400" />
                        ต่อ {extension.extension_days} วัน ({formatDate(extension.original_end_date)} → {formatDate(extension.new_end_date)})
                      </div>

                      {/* BUG-PG-EXTFEE-ZEROTOTAL: new_rental_fee = null แปลว่าคำขอนี้ไม่ได้แตะค่าเช่า
                          (ไม่ใช่ค่าเช่าหายไป) → แสดงค่าเช่าเดิมพร้อมกำกับว่าคงเดิม แทนลูกศรชี้ไป '-' */}
                      {(toNumber(extension.new_rental_fee) != null || !!toNumber(extension.original_rental_fee)) && (
                        <div className="flex items-center text-secondary-600">
                          <RiMoneyDollarCircleLine className="w-4 h-4 mr-2 text-secondary-400" />
                          ค่าเช่า: {formatOriginalFee(extension.original_rental_fee)}
                          {toNumber(extension.new_rental_fee) == null ? (
                            <span className="ml-1 text-secondary-500">(ไม่เปลี่ยนแปลง)</span>
                          ) : (
                            <>
                              {' → '}{formatMoney(extension.new_rental_fee)}
                              {(() => {
                                // เทียบเป็นตัวเลขเสมอ — เดิมเทียบ string ตรงๆ ("9000.00" > "10000.00"
                                // เป็น true) ทำให้ค่าเช่าที่ "ลดลง" ถูกแสดงเป็นเพิ่มขึ้น (แดง/+)
                                const oldFee = toNumber(extension.original_rental_fee);
                                const newFee = toNumber(extension.new_rental_fee);
                                if (!oldFee || newFee == null || newFee === oldFee) return null;
                                const pct = ((newFee - oldFee) / oldFee) * 100;
                                return (
                                  <span className={`ml-2 text-xs ${pct > 0 ? 'text-danger-600' : 'text-success-600'}`}>
                                    ({pct > 0 ? '+' : ''}{pct.toFixed(1)}%)
                                  </span>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      )}

                      {extension.reason && (
                        <div className="flex items-start text-secondary-600">
                          <RiFileTextLine className="w-4 h-4 mr-2 text-secondary-400 mt-0.5" />
                          <span>เหตุผล: {extension.reason}</span>
                        </div>
                      )}

                      <div className="flex items-center text-secondary-600">
                        <RiUserLine className="w-4 h-4 mr-2 text-secondary-400" />
                        ขอโดย: {extension.requested_by?.full_name || extension.requested_by?.username || '-'}
                        {extension.approved_at && extension.approved_by && (
                          <span className="ml-2">
                            | {extension.is_approved === false ? 'ปฏิเสธโดย' : 'อนุมัติโดย'}: {extension.approved_by.full_name || extension.approved_by.username}
                          </span>
                        )}
                      </div>

                      {extension.is_approved === false && extension.reject_reason && (
                        <div className="flex items-start text-danger-700">
                          <RiCloseCircleLine className="w-4 h-4 mr-2 text-danger-500 mt-0.5" />
                          <span>เหตุผลที่ปฏิเสธ: {extension.reject_reason}</span>
                        </div>
                      )}

                      {extension.notes && (
                        <div className="mt-2 p-2 bg-secondary-50 rounded text-secondary-600">
                          <p className="text-xs">หมายเหตุ: {extension.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Current Status */}
          <div className="relative">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-primary-600 rounded-full animate-pulse"></div>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <h5 className="font-medium text-primary-900 mb-1">สถานะปัจจุบัน</h5>
                  <p className="text-sm text-primary-700">
                    สิ้นสุดวันที่ {formatDate(history.current_end_date)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalExtensionHistoryComponent;